// Bloco só leitura dos agentes de IA de uma conexão (spec agente-conexao-filas
// §3.3). O vínculo agente ↔ conexão/filas é editado só no modal do agente
// (Talk.ai); aqui o usuário vê quem atende e vai para /prompts.
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { makeStyles, alpha } from "@material-ui/core/styles";
import { OpenInNew } from "@material-ui/icons";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { agentesDaConexao } from "./agentesDaConexaoRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const aeros = theme.palette.aeros || {};
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const purple = stateText(theme, "#C08BFF", "purple");
  return {
    root: { display: "flex", flexDirection: "column", gap: 8, minWidth: 0 },
    title: {
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      margin: 0,
    },
    hint: {
      fontSize: 11,
      color: aeros.textTertiary || theme.palette.text.secondary,
      margin: 0,
    },
    list: {
      listStyle: "none",
      margin: 0,
      padding: 0,
      display: "flex",
      flexDirection: "column",
      gap: 8,
    },
    item: {
      padding: "10px 14px",
      borderRadius: 12,
      border: `1px solid ${border}`,
      backgroundColor: aeros.surface || theme.palette.background.paper,
      minWidth: 0,
    },
    name: {
      fontSize: 13,
      fontWeight: 600,
      color: theme.palette.text.primary,
      overflowWrap: "anywhere",
    },
    chips: {
      display: "flex",
      flexWrap: "wrap",
      gap: 6,
      marginTop: 6,
      padding: 0,
      listStyle: "none",
    },
    chip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      maxWidth: "100%",
      padding: "2px 8px",
      borderRadius: 999,
      fontSize: 11.5,
      lineHeight: 1.6,
      color: theme.palette.text.primary,
      backgroundColor: aeros.surface2 || theme.palette.action.hover,
      border: `1px solid ${border}`,
      overflowWrap: "anywhere",
    },
    dot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
    chipPreQueue: {
      color: purple,
      backgroundColor: alpha(AEROS.defaultPrimary, 0.12),
      border: `1px solid ${alpha(AEROS.defaultPrimary, 0.3)}`,
    },
    chipPending: {
      color: stateText(theme, AEROS.warning, "warning"),
      backgroundColor: alpha(AEROS.warning, 0.1),
      border: `1px solid ${alpha(AEROS.warning, 0.25)}`,
    },
    empty: {
      padding: "12px 14px",
      borderRadius: 12,
      border: `1px dashed ${
        isLight ? aeros.borderStrong || theme.palette.divider : "rgba(255,255,255,0.16)"
      }`,
      fontSize: 12.5,
      color: theme.palette.text.secondary,
      margin: 0,
    },
    link: {
      alignSelf: "flex-start",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      minHeight: 44,
      padding: "0 4px",
      fontSize: 13,
      fontWeight: 600,
      color: purple,
      textDecoration: "none",
      borderRadius: 8,
      "&:hover": { textDecoration: "underline" },
      "&:focus-visible": { outline: `2px solid ${purple}`, outlineOffset: 2 },
      "& .MuiSvgIcon-root": { fontSize: 15 },
    },
  };
});

const AgentesDaConexao = ({ whatsappId, queueIds, companyId }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext) || {};
  const [prompts, setPrompts] = useState([]);
  const [filas, setFilas] = useState([]);
  const [promptAntesDaFila, setPromptAntesDaFila] = useState(null);
  const [carregando, setCarregando] = useState(Boolean(whatsappId));

  // Conexão de outra empresa (modal do super admin): GET /prompt e /queue só
  // enxergam a empresa do usuário, então não dá para listar os agentes dela.
  const outraEmpresa =
    companyId !== undefined &&
    companyId !== null &&
    user?.companyId !== undefined &&
    Number(companyId) !== Number(user.companyId);

  useEffect(() => {
    if (!whatsappId || outraEmpresa) {
      setCarregando(false);
      return undefined;
    }
    let ativo = true;
    setCarregando(true);
    Promise.all([
      api.get("/prompt", { params: { all: true } }),
      api.get("/queue"),
      api.get(`/whatsapp/${whatsappId}`, { params: { session: 0 } }),
    ])
      .then(([{ data: p }, { data: q }, { data: w }]) => {
        if (!ativo) return;
        setPrompts(Array.isArray(p?.prompts) ? p.prompts : []);
        setFilas(Array.isArray(q) ? q : []);
        setPromptAntesDaFila(w?.promptId ?? null);
      })
      .catch(() => {
        // Bloco informativo: falha aqui não pode travar o modal da conexão.
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, [whatsappId, outraEmpresa]);

  const agentes = useMemo(
    () =>
      agentesDaConexao({
        prompts,
        filas,
        whatsappId,
        queueIdsDaConexao: queueIds,
        promptAntesDaFila,
      }),
    [prompts, filas, whatsappId, queueIds, promptAntesDaFila]
  );

  const link = (
    <RouterLink to="/prompts" className={classes.link}>
      {i18n.t("aeros.connections.agents.manage")}
      <OpenInNew aria-hidden="true" />
    </RouterLink>
  );

  let conteudo;
  if (!whatsappId) {
    conteudo = <p className={classes.empty}>{i18n.t("aeros.connections.agents.newConnection")}</p>;
  } else if (outraEmpresa) {
    conteudo = <p className={classes.empty}>{i18n.t("aeros.connections.agents.otherCompany")}</p>;
  } else if (carregando) {
    conteudo = (
      <p className={classes.empty} role="status">
        {i18n.t("aeros.connections.agents.loading")}
      </p>
    );
  } else if (agentes.length === 0) {
    conteudo = <p className={classes.empty}>{i18n.t("aeros.connections.agents.empty")}</p>;
  } else {
    conteudo = (
      <ul className={classes.list} aria-labelledby="conexao-agentes-titulo">
        {agentes.map((a) => (
          <li key={a.id} className={classes.item}>
            <div className={classes.name}>{a.name}</div>
            <ul className={classes.chips}>
              {a.filas.map((f) => (
                <li key={f.id} className={classes.chip}>
                  {f.color && (
                    <span
                      className={classes.dot}
                      style={{ backgroundColor: f.color }}
                      aria-hidden="true"
                    />
                  )}
                  {f.name}
                </li>
              ))}
              {a.antesDaFila && (
                <li className={`${classes.chip} ${classes.chipPreQueue}`}>
                  {i18n.t("aeros.connections.agents.preQueue")}
                </li>
              )}
              {a.legado && (
                <li
                  className={`${classes.chip} ${classes.chipPending}`}
                  title={i18n.t("aeros.talkai.vinculo.pendingHint")}
                >
                  {i18n.t("aeros.talkai.vinculo.pending")}
                </li>
              )}
            </ul>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <section className={classes.root} aria-labelledby="conexao-agentes-titulo">
      <h3 id="conexao-agentes-titulo" className={classes.title}>
        {i18n.t("aeros.connections.agents.title")}
      </h3>
      <p className={classes.hint}>{i18n.t("aeros.connections.agents.hint")}</p>
      {conteudo}
      {!outraEmpresa && link}
    </section>
  );
};

export default AgentesDaConexao;
